import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, ArrowUpRight } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/i18n";

export const ChatWidget = () => {
  const location = useLocation();
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [prestation, setPrestation] = useState(null);
  const sessionRef = useRef(null);
  const listRef = useRef(null);

  if (!sessionRef.current) {
    sessionRef.current = localStorage.getItem("ncs_chat_session") || crypto.randomUUID();
    localStorage.setItem("ncs_chat_session", sessionRef.current);
  }

  useEffect(() => {
    if (messages.length === 0) setMessages([{ role: "assistant", text: t("ch.welcome") }]);
  }, [t, messages.length]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  if (location.pathname === "/admin") return null;

  const buildResume = () => {
    const userMsgs = messages.filter((m) => m.role === "user").map((m) => m.text);
    if (!userMsgs.length) return "";
    const joined = userMsgs.join(" — ");
    return (t("ch.resumePrefix") + joined).slice(0, 500);
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: content }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: content, lang }),
      });
      if (!res.ok || !res.body) throw new Error("stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = part.slice(6);
          if (data === "[DONE]") continue;
          try {
            const parsed = JSON.parse(data);
            if (parsed.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + parsed.delta };
                return copy;
              });
            } else if (parsed.prestation) {
              setPrestation(parsed.prestation);
            } else if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch (e) {
            if (e.message !== "stream") throw e;
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: t("ch.error") };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [t("ch.s1"), t("ch.s2"), t("ch.s3")];
  const resume = buildResume();
  const devisLink = `/contact?prestation=${encodeURIComponent(prestation || "")}${resume ? `&resume=${encodeURIComponent(resume)}` : ""}`;

  return (
    <>
      <motion.button
        data-testid="chat-open-btn"
        onClick={() => setOpen(true)}
        initial={{ scale: 0, rotate: -20 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 2.2, type: "spring", stiffness: 200, damping: 15 }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        aria-label="Assistant créatif"
        className={`fixed bottom-6 left-6 z-[70] w-14 h-14 rounded-full bg-gold text-black flex items-center justify-center shadow-[0_0_30px_rgba(212,175,55,0.35)] ${open ? "hidden" : ""}`}
      >
        <Sparkles size={22} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-6 left-6 z-[70] w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[75vh] flex flex-col bg-[#0c0c0c]/95 backdrop-blur-xl border border-gold/30 shadow-2xl"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div>
                <p className="font-serif text-lg leading-none">{t("ch.title")}</p>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-gold/70 mt-1.5">
                  {t("ch.powered")}
                </p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close-btn" aria-label="Fermer"
                className="text-white/50 hover:text-gold transition-colors duration-300">
                <X size={20} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gold text-black"
                      : "bg-white/[0.06] text-white/85 border border-white/10"
                  }`}>
                    {m.text || (loading && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce [animation-delay:0.15s]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce [animation-delay:0.3s]" />
                      </span>
                    ) : "")}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {suggestions.map((s, i) => (
                    <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${i}`}
                      className="text-xs border border-gold/40 text-gold/90 px-3 py-2 transition-colors duration-300 hover:bg-gold hover:text-black">
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 pb-2 space-y-3">
              {prestation && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
                  <Link
                    to={devisLink}
                    data-testid="chat-prestation-cta"
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between gap-2 bg-gold text-black px-4 py-3 text-xs uppercase tracking-[0.12em] font-medium transition-colors duration-300 hover:bg-gold-light"
                  >
                    {t("ch.devis")} — {lang === "en" ? (prestation === "Customisation" ? "Customization" : prestation) : prestation} <ArrowUpRight size={14} />
                  </Link>
                </motion.div>
              )}
              <Link to="/contact" data-testid="chat-devis-link"
                className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-gold hover:text-gold-light transition-colors duration-300">
                {t("ch.devis")} <ArrowUpRight size={13} />
              </Link>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-3 px-5 py-4 border-t border-white/10">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder={t("ch.inputPh")}
                className="flex-1 bg-transparent text-sm text-white placeholder:text-white/30 focus:outline-none"
              />
              <button type="submit" data-testid="chat-send-btn" disabled={loading || !input.trim()} aria-label="Envoyer"
                className="w-10 h-10 rounded-full bg-gold text-black flex items-center justify-center transition-opacity duration-300 disabled:opacity-40">
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
